import type { Components, CssVarsTheme, Theme } from "@mui/material/styles";
import type {} from "@mui/x-charts/themeAugmentation";
import type {} from "@mui/x-data-grid/themeAugmentation";

import { accent, danger, density, neutral, radius, success, warning, type Ramp } from "./tokens";

/** 语义色徽标（Chip filled）：浅底 + 深字 + 同色描边，五种语气轻重一致。 */
function badge(ramp: Ramp) {
  return ({ theme }: { theme: Theme }) => ({
    backgroundColor: ramp[50],
    borderColor: ramp[200],
    color: ramp[700],
    ...theme.applyStyles("dark", {
      backgroundColor: ramp[900],
      borderColor: ramp[800],
      color: ramp[200],
    }),
  });
}

const neutralBadge = ({ theme }: { theme: Theme }) => ({
  backgroundColor: neutral[50],
  borderColor: neutral[200],
  color: neutral[600],
  ...theme.applyStyles("dark", {
    backgroundColor: neutral[850],
    borderColor: neutral[800],
    color: neutral[400],
  }),
});

/** 开了 cssVariables 之后主题一定带 vars；拆成独立文件后类型里拿不到这个保证，这里收口一次。 */
type Vars = CssVarsTheme["vars"];
const v = (theme: Theme): Vars => (theme as Theme & { vars: Vars }).vars;

/** 只覆盖确实用到的组件；密度与色值全部取自 ./tokens，页面里不写死像素。 */
export const components: Components<Theme> = {
      MuiCssBaseline: {
        styleOverrides: {
          "*, *::before, *::after": { boxSizing: "border-box" },
          html: { WebkitFontSmoothing: "antialiased", MozOsxFontSmoothing: "grayscale" },
          body: { fontVariantNumeric: "tabular-nums" },
          // 细滚动条，长表格里不抢视觉。
          "*::-webkit-scrollbar": { width: 10, height: 10 },
          "*::-webkit-scrollbar-thumb": {
            borderRadius: radius.pill,
            border: "3px solid transparent",
            backgroundClip: "content-box",
            backgroundColor: neutral[300],
          },
          "*::-webkit-scrollbar-thumb:hover": { backgroundColor: neutral[400] },
        },
      },

      MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { root: { backgroundImage: "none" } } },

      MuiCard: {
        defaultProps: { variant: "outlined" },
        styleOverrides: {
          root: ({ theme }) => ({
            padding: density.cardPadding,
            borderRadius: radius.lg,
            border: `1px solid ${v(theme).palette.divider}`,
            backgroundColor: v(theme).palette.background.paper,
            boxShadow: "none",
          }),
        },
      },
      MuiCardContent: { styleOverrides: { root: { padding: 0, "&:last-child": { paddingBottom: 0 } } } },
      MuiCardHeader: { styleOverrides: { root: { padding: 0 } } },
      MuiCardActions: { styleOverrides: { root: { padding: 0 } } },
      MuiCardActionArea: { styleOverrides: { root: { borderRadius: radius.lg } } },

      MuiButton: {
        defaultProps: { disableElevation: true, size: "small" },
        styleOverrides: {
          root: { borderRadius: radius.md, minHeight: density.control, paddingInline: 12, whiteSpace: "nowrap" },
          sizeSmall: { minHeight: density.controlSmall, paddingInline: 10, fontSize: 13 },
          outlined: ({ theme }) => ({
            borderColor: v(theme).palette.divider,
            color: v(theme).palette.text.primary,
            "&:hover": { borderColor: neutral[300], backgroundColor: v(theme).palette.action.hover },
          }),
          text: ({ theme }) => ({ color: v(theme).palette.text.secondary }),
        },
      },
      MuiIconButton: {
        defaultProps: { size: "small" },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: radius.md,
            color: v(theme).palette.text.secondary,
            "&:hover": { backgroundColor: v(theme).palette.action.hover, color: v(theme).palette.text.primary },
          }),
        },
      },
      MuiToggleButtonGroup: {
        defaultProps: { size: "small" },
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: v(theme).palette.background.paper,
            border: `1px solid ${v(theme).palette.divider}`,
            borderRadius: radius.md,
            padding: 2,
            gap: 2,
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            border: 0,
            borderRadius: `${radius.sm}px !important`,
            minHeight: 24,
            paddingInline: 10,
            fontSize: 12,
            fontWeight: 500,
            color: v(theme).palette.text.secondary,
            "&.Mui-selected": {
              backgroundColor: v(theme).palette.background.default,
              color: v(theme).palette.text.primary,
              boxShadow: v(theme).shadows[1],
            },
          }),
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: density.control,
            borderRadius: radius.md,
            backgroundColor: v(theme).palette.background.paper,
            fontSize: 13,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: v(theme).palette.divider },
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: neutral[300] },
          }),
          input: { paddingBlock: 6, paddingInline: 10, height: "auto" },
        },
      },
      MuiTextField: { defaultProps: { size: "small" } },
      MuiSelect: { defaultProps: { size: "small" } },
      MuiInputLabel: { styleOverrides: { root: { fontSize: 13 } } },
      MuiFormControlLabel: { styleOverrides: { label: { fontSize: 13 } } },
      MuiInputAdornment: { styleOverrides: { root: ({ theme }) => ({ color: v(theme).palette.text.disabled }) } },
      MuiSwitch: { defaultProps: { size: "small" } },

      MuiChip: {
        defaultProps: { size: "small", variant: "filled" },
        styleOverrides: {
          root: {
            height: 20,
            borderRadius: radius.sm,
            border: "1px solid",
            fontSize: 12,
            fontWeight: 500,
          },
          label: { paddingInline: 6 },
          icon: { marginLeft: 4, marginRight: -2, fontSize: 13 },
        },
        variants: [
          { props: { variant: "filled", color: "default" }, style: neutralBadge },
          { props: { variant: "filled", color: "primary" }, style: badge(accent) },
          { props: { variant: "filled", color: "info" }, style: badge(accent) },
          { props: { variant: "filled", color: "success" }, style: badge(success) },
          { props: { variant: "filled", color: "warning" }, style: badge(warning) },
          { props: { variant: "filled", color: "error" }, style: badge(danger) },
        ],
      },

      MuiTabs: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: 36,
            borderBottom: `1px solid ${v(theme).palette.divider}`,
          }),
          indicator: { height: 2 },
          list: { gap: 4 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: 36,
            paddingInline: 10,
            fontSize: 13,
            fontWeight: 500,
            color: v(theme).palette.text.secondary,
            "&:hover": { color: v(theme).palette.text.primary },
            "&.Mui-selected": { color: v(theme).palette.text.primary, fontWeight: 600 },
          }),
        },
      },

      MuiTooltip: {
        defaultProps: { arrow: false, enterDelay: 300 },
        styleOverrides: {
          tooltip: {
            backgroundColor: neutral[800],
            borderRadius: radius.md,
            fontSize: 12,
            lineHeight: 1.5,
            paddingInline: 8,
            paddingBlock: 6,
            maxWidth: 360,
          },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: radius.md,
            border: `1px solid ${v(theme).palette.divider}`,
            boxShadow: v(theme).shadows[2],
            marginTop: 4,
          }),
          list: { paddingBlock: 4 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: { minHeight: 30, fontSize: 13, borderRadius: radius.sm, marginInline: 4, paddingInline: 8 },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            lineHeight: "28px",
            color: v(theme).palette.text.disabled,
            backgroundColor: "transparent",
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            minHeight: density.sidebarItem,
            borderRadius: radius.md,
            paddingInline: 8,
            gap: 8,
            color: v(theme).palette.text.secondary,
            "&:hover": { backgroundColor: v(theme).palette.action.hover, color: v(theme).palette.text.primary },
            "&.Mui-selected": {
              backgroundColor: v(theme).palette.action.selected,
              color: v(theme).palette.primary.main,
              fontWeight: 600,
              "&:hover": { backgroundColor: v(theme).palette.action.selected },
            },
          }),
        },
      },
      MuiListItemIcon: { styleOverrides: { root: { minWidth: 0, color: "inherit", "& svg": { fontSize: 17 } } } },
      MuiListItemText: { styleOverrides: { primary: { fontSize: 13, fontWeight: "inherit" } } },

      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundImage: "none",
            borderColor: v(theme).palette.divider,
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: radius.lg,
            border: `1px solid ${v(theme).palette.divider}`,
            boxShadow: v(theme).shadows[3],
          }),
        },
      },
      MuiDialogTitle: { styleOverrides: { root: { fontSize: 15, fontWeight: 600, padding: "14px 16px" } } },
      MuiDialogContent: { styleOverrides: { root: { padding: "0 16px 4px" } } },
      MuiDialogActions: { styleOverrides: { root: { padding: 12, gap: 8 } } },

      MuiAlert: {
        defaultProps: { variant: "outlined" },
        styleOverrides: {
          root: { borderRadius: radius.md, paddingBlock: 4, fontSize: 13, alignItems: "center" },
          icon: { paddingBlock: 6, marginRight: 8, "& svg": { fontSize: 18 } },
          message: { paddingBlock: 6 },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: ({ theme }) => ({ height: 4, borderRadius: radius.pill, backgroundColor: v(theme).palette.action.hover }),
          bar: { borderRadius: radius.pill },
        },
      },
      MuiSkeleton: { defaultProps: { animation: "wave" }, styleOverrides: { root: { borderRadius: radius.sm } } },
      MuiDivider: { styleOverrides: { root: ({ theme }) => ({ borderColor: v(theme).palette.divider }) } },
      MuiLink: { defaultProps: { underline: "hover" }, styleOverrides: { root: { fontWeight: 500 } } },
      MuiAvatar: { styleOverrides: { root: { fontSize: 12, fontWeight: 600 } } },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: ({ theme }) => ({ fontSize: 12, color: v(theme).palette.text.secondary }),
          separator: { marginInline: 4 },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: 13,
            paddingBlock: 7,
            paddingInline: 10,
            borderColor: v(theme).palette.divider,
          }),
          head: ({ theme }) => ({
            fontSize: 12,
            fontWeight: 600,
            color: v(theme).palette.text.secondary,
            backgroundColor: v(theme).palette.background.default,
          }),
        },
      },

      MuiDataGrid: {
        defaultProps: { disableColumnMenu: true, rowHeight: density.rowHeight, columnHeaderHeight: density.headerHeight },
        styleOverrides: {
          root: ({ theme }) => ({
            border: 0,
            "--DataGrid-overlayHeight": "220px",
            "--DataGrid-rowBorderColor": v(theme).palette.divider,
            fontSize: 13,
          }),
          columnHeaders: { borderRadius: 0 },
          columnHeader: ({ theme }) => ({
            backgroundColor: v(theme).palette.background.default,
            paddingInline: 10,
            "&:focus, &:focus-within": { outline: "none" },
          }),
          columnHeaderTitle: ({ theme }) => ({ fontSize: 12, fontWeight: 600, color: v(theme).palette.text.secondary }),
          cell: ({ theme }) => ({
            paddingInline: 10,
            borderTopColor: v(theme).palette.divider,
            "&:focus, &:focus-within": { outline: "none" },
          }),
          row: ({ theme }) => ({ "&:hover": { backgroundColor: v(theme).palette.action.hover } }),
          footerContainer: ({ theme }) => ({
            minHeight: 44,
            borderTop: `1px solid ${v(theme).palette.divider}`,
            backgroundColor: v(theme).palette.background.paper,
          }),
          overlayWrapperInner: ({ theme }) => ({ color: v(theme).palette.text.disabled }),
        },
      },

      MuiChartsAxis: {
        styleOverrides: {
          root: ({ theme }) => ({
            "& .MuiChartsAxis-tickLabel": { fontSize: 11, fill: v(theme).palette.text.disabled },
            "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: v(theme).palette.divider },
          }),
        },
      },
      MuiChartsGrid: {
        styleOverrides: {
          root: ({ theme }) => ({ "& .MuiChartsGrid-line": { stroke: v(theme).palette.divider, strokeDasharray: "3 3" } }),
        },
      },
      MuiChartsTooltip: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: radius.md,
            border: `1px solid ${v(theme).palette.divider}`,
            boxShadow: v(theme).shadows[2],
            fontSize: 12,
          }),
        },
      },
};
